// src/hooks/useDashboard.js - VERSION AMÉLIORÉE AVEC FALLBACK
import { useState, useEffect, useCallback, useRef } from 'react';
import { ethers } from 'ethers';
import { useWeb3 } from '../context/Web3Context';

// Constants (réutilisés d'useAdminMonitoring)
const PROXY_ADDRESS = "0xC3b2990027217b9970b2d526aa11Ba3f223eb39C";
const IMPLEMENTATION_V2_ADDRESS = "0xdb2e16605c672bd0d743142e10ce2c1b12a876a4";
const UNISWAP_PAIR_ADDRESS = "0x8a1D8f57261e8832CE1D7C525Df76dbe002B2e25";
const WETH_ADDRESS = "0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2";

// Fallback RPC URLs
const FALLBACK_RPC_URLS = [
  "https://eth-mainnet.g.alchemy.com/v2/lr9JPx6sJhRWwTYZngq-gJwUc-rEpB_I",
  "https://rpc.ankr.com/eth",
  "https://eth.llamarpc.com"
];

// ABIs minimaux
const MINIMAL_ABI = [
  // ERC20
  "function name() view returns (string)",
  "function symbol() view returns (string)",
  "function decimals() view returns (uint8)",
  "function totalSupply() view returns (uint256)",
  "function balanceOf(address) view returns (uint256)",
  
  // Contract
  "function tradingEnabled() view returns (bool)",
  "function initialLiquidityLocked() view returns (bool)",
  "function totalBurned() view returns (uint256)",
  "function currentRewardsPool() view returns (uint256)",
  "function totalStaked() view returns (uint256)",
  "function totalLPStaked() view returns (uint256)",
  "function MAX_BURN_SUPPLY() view returns (uint256)"
];

const PAIR_ABI = [
  "function token0() view returns (address)",
  "function token1() view returns (address)",
  "function getReserves() view returns (uint112,uint112,uint32)",
  "function totalSupply() view returns (uint256)"
];

export const useDashboard = () => {
  // Récupérer le provider du contexte, mais il peut être null si MetaMask n'est pas connecté
  const { provider: web3Provider } = useWeb3();
  
  // Provider interne qui utilisera soit le provider du contexte, soit un fallback
  const [provider, setProvider] = useState(null);
  
  // Refs pour les contrats
  const contractRef = useRef(null);
  const uniswapContractRef = useRef(null);
  
  // État principal
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const [debugLogs, setDebugLogs] = useState([]);
  
  // Données du contrat - seulement les données publiques
  const [contractState, setContractState] = useState({
    name: 'GR33DVAULT',
    symbol: 'GR33D',
    totalSupply: '5000000',
    implementation: '',
    isValidImplementation: false,
    tradingEnabled: true,
    liquidityLocked: false,
    contractBalance: '4850000',
    totalBurned: '780',
    rewardsPool: '999000',
    totalStaked: '600',
    totalLPStaked: '1.25',
    maxBurnSupply: '2000000',
    burnProgress: 0.039,
    burnRemaining: '1999220',
    uniswapReserves: {
      gr33dReserve: '147500',
      ethReserve: '0.88',
      totalLiquidity: '3000',
      ethPrice: 3000
    }
  });

  // Fonction d'ajout de logs (simplifiée)
  const addLog = useCallback((type, message, data = {}) => {
    const log = {
      type,
      message,
      data,
      timestamp: new Date().toISOString()
    };
    console.log(`[DASHBOARD][${type.toUpperCase()}] ${message}`, data);
    setDebugLogs(prev => [log, ...prev.slice(0, 19)]); // Garder les 20 derniers logs
  }, []);

  // Initialisation du provider - MAINTENANT AVEC FALLBACK
  useEffect(() => {
    const initializeProvider = async () => {
      // Si web3Provider est disponible (MetaMask connecté), l'utiliser
      if (web3Provider) {
        setProvider(web3Provider);
        addLog('provider', 'Utilisant le provider depuis Web3Context (MetaMask)');
        return;
      }
      
      // Sinon, essayer de créer un provider fallback avec l'un des RPC
      addLog('provider', 'Aucun provider connecté, tentative de fallback RPC');
      
      // Essayer chaque URL RPC jusqu'à ce qu'une fonctionne
      for (const rpcUrl of FALLBACK_RPC_URLS) {
        try {
          const fallbackProvider = new ethers.providers.JsonRpcProvider(rpcUrl);
          
          // Tester que le provider fonctionne avec un appel simple
          const blockNumber = await fallbackProvider.getBlockNumber();
          
          if (blockNumber > 0) {
            setProvider(fallbackProvider);
            addLog('provider', `Provider fallback initialisé avec ${rpcUrl}`, { blockNumber });
            return;
          }
        } catch (error) {
          addLog('error', `Échec d'initialisation du provider avec ${rpcUrl}`, { error: error.message });
          // Continuer avec l'URL suivante
        }
      }
      
      // Si tous les RPC échouent, utiliser les données en cache s'il y en a
      addLog('warn', 'Tous les providers ont échoué, utilisation des données en cache');
      
      // Vérifier si des données ont été mises en cache précédemment
      const cachedData = localStorage.getItem('gr33d_dashboard_cache');
      if (cachedData) {
        try {
          const parsedData = JSON.parse(cachedData);
          setContractState(parsedData);
          setLastUpdateTime(new Date(parsedData.cacheTimestamp || Date.now()));
          addLog('cache', 'Données récupérées depuis le cache local');
        } catch (error) {
          addLog('error', 'Erreur de parsing du cache', { error: error.message });
        }
      }
      
      setIsLoading(false);
    };
    
    initializeProvider();
  }, [web3Provider, addLog]);

  // Fonction d'initialisation des contrats - AVEC GESTION ROBUSTE DES ERREURS
  const initializeContracts = useCallback(async () => {
    if (!provider) {
      addLog('warn', 'Provider non disponible pour l\'initialisation des contrats');
      return { success: false, error: 'Provider manquant' };
    }
    
    try {
      addLog('init', 'Initialisation des contrats');
      
      // Création du contrat principal
      const greedContract = new ethers.Contract(
        PROXY_ADDRESS,
        MINIMAL_ABI,
        provider
      );
      
      // Test du contrat
      try {
        const name = await greedContract.name();
        addLog('success', 'Contrat principal connecté', { name });
        contractRef.current = greedContract;
      } catch (err) {
        addLog('error', 'Échec de connexion au contrat principal', { error: err.message });
        return { success: false, error: 'Échec de connexion au contrat' };
      }
      
      // Création du contrat de paire Uniswap
      try {
        const pairContract = new ethers.Contract(
          UNISWAP_PAIR_ADDRESS,
          PAIR_ABI,
          provider
        );
        
        // Test simple
        const [token0, reserves] = await Promise.all([
          pairContract.token0().catch(() => null),
          pairContract.getReserves().catch(() => null)
        ]);
        
        if (token0 && reserves) {
          addLog('success', 'Contrat Uniswap connecté', { token0 });
          uniswapContractRef.current = pairContract;
        } else {
          addLog('warn', 'Connexion partielle au contrat Uniswap');
        }
      } catch (pairErr) {
        addLog('error', 'Échec de connexion au contrat Uniswap', { error: pairErr.message });
      }
      
      return { success: true };
    } catch (error) {
      addLog('error', 'Erreur globale lors de l\'initialisation des contrats', { error: error.message });
      return { success: false, error: error.message };
    }
  }, [provider, addLog]);
  
  // Récupération des prix ETH depuis une API externe
  const fetchEthPrice = useCallback(async () => {
    try {
      addLog('eth-price', 'Récupération du prix ETH');
      
      // Utiliser localStorage pour cacher le prix
      const cachedPrice = localStorage.getItem('gr33d_eth_price_cache');
      const cachedTimestamp = localStorage.getItem('gr33d_eth_price_timestamp');
      
      if (cachedPrice && cachedTimestamp && (Date.now() - Number(cachedTimestamp)) < 1800000) {
        addLog('eth-price', 'Prix ETH récupéré depuis le cache', { price: cachedPrice });
        return parseFloat(cachedPrice);
      }
      
      // Essayer avec CoinGecko
      try {
        const response = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=ethereum&vs_currencies=usd');
        if (response.ok) {
          const data = await response.json();
          if (data.ethereum?.usd) {
            const price = parseFloat(data.ethereum.usd);
            
            // Sauvegarder dans le cache
            localStorage.setItem('gr33d_eth_price_cache', price.toString());
            localStorage.setItem('gr33d_eth_price_timestamp', Date.now().toString());
            
            addLog('eth-price', 'Prix ETH récupéré via CoinGecko', { price });
            return price;
          }
        }
      } catch (geckoError) {
        addLog('warn', 'Échec récupération prix ETH via CoinGecko', { error: geckoError.message });
      }
      
      // Fallback Binance
      try {
        const response = await fetch('https://api.binance.com/api/v3/ticker/price?symbol=ETHUSDT');
        if (response.ok) {
          const data = await response.json();
          if (data.price) {
            const price = parseFloat(data.price);
            
            // Sauvegarder dans le cache
            localStorage.setItem('gr33d_eth_price_cache', price.toString());
            localStorage.setItem('gr33d_eth_price_timestamp', Date.now().toString());
            
            addLog('eth-price', 'Prix ETH récupéré via Binance', { price });
            return price;
          }
        }
      } catch (binanceError) {
        addLog('warn', 'Échec récupération prix ETH via Binance', { error: binanceError.message });
      }
      
      // Dernier recours: valeur approximative
      return contractState.uniswapReserves.ethPrice || 3000;
    } catch (error) {
      addLog('error', 'Erreur globale lors de la récupération du prix ETH', { error: error.message });
      return contractState.uniswapReserves.ethPrice || 3000; // Valeur par défaut
    }
  }, [addLog, contractState.uniswapReserves.ethPrice]);
  
  // Récupération des informations de base du contrat
  const fetchBasicContractInfo = useCallback(async () => {
    if (!contractRef.current) {
      addLog('warn', 'Contrat non initialisé pour fetchBasicContractInfo');
      return false;
    }
    
    addLog('contract', 'Récupération des informations de base du contrat');
    const contract = contractRef.current;
    const data = {};
    
    // 1. Nom & Symbole
    try {
      [data.name, data.symbol] = await Promise.all([
        contract.name(),
        contract.symbol()
      ]);
      addLog('success', 'Nom et symbole récupérés', { name: data.name, symbol: data.symbol });
    } catch (nameErr) {
      addLog('error', 'Échec récupération nom/symbole', { error: nameErr.message });
      // Utiliser les valeurs par défaut
      data.name = contractState.name;
      data.symbol = contractState.symbol;
    }
    
    // 2. Supply Total
    try {
      const totalSupply = await contract.totalSupply();
      data.totalSupply = ethers.utils.formatEther(totalSupply);
      addLog('success', 'Supply total récupéré', { totalSupply: data.totalSupply });
    } catch (supplyErr) {
      addLog('error', 'Échec récupération supply total', { error: supplyErr.message });
      data.totalSupply = contractState.totalSupply; // Garder la valeur actuelle
    }
    
    // 3. Vérification de l'implémentation
    try {
      // Slot standard pour les proxies transparents et UUPS
      const slot = "0x360894a13ba1a3210667c828492db98dca3e2076cc3735a920a3ca505d382bbc";
      const implementationData = await provider.getStorageAt(PROXY_ADDRESS, slot);
      
      // Extraction de l'adresse d'implémentation
      const implementation = "0x" + implementationData.slice(26).toLowerCase();
      data.implementation = implementation;
      data.isValidImplementation = implementation === IMPLEMENTATION_V2_ADDRESS.toLowerCase();
      
      addLog('success', 'Implémentation vérifiée', { 
        implementation, 
        expected: IMPLEMENTATION_V2_ADDRESS.toLowerCase(),
        isValid: data.isValidImplementation
      });
    } catch (implErr) {
      addLog('error', 'Échec vérification implémentation', { error: implErr.message });
      // Garder les valeurs actuelles
      data.implementation = contractState.implementation;
      data.isValidImplementation = contractState.isValidImplementation;
    }
    
    // Mise à jour de l'état
    setContractState(prevState => ({
      ...prevState,
      ...data
    }));
    
    return true;
  }, [provider, contractState, addLog]);
  
  // Récupération des états du contrat
  const fetchContractStates = useCallback(async () => {
    if (!contractRef.current) {
      addLog('warn', 'Contrat non initialisé pour fetchContractStates');
      return false;
    }
    
    addLog('contract', 'Récupération des états du contrat');
    const contract = contractRef.current;
    const data = {};
    
    // Tester toutes les méthodes en parallèle
    try {
      const [tradingEnabled, liquidityLocked] = await Promise.all([
        contract.tradingEnabled().catch(() => contractState.tradingEnabled),
        contract.initialLiquidityLocked().catch(() => contractState.liquidityLocked)
      ]);
      
      data.tradingEnabled = tradingEnabled;
      data.liquidityLocked = liquidityLocked;
      
      addLog('success', 'États du contrat récupérés', { 
        tradingEnabled: data.tradingEnabled, 
        liquidityLocked: data.liquidityLocked 
      });
    } catch (error) {
      addLog('error', 'Échec récupération des états du contrat', { error: error.message });
      // Garder les valeurs actuelles
      data.tradingEnabled = contractState.tradingEnabled;
      data.liquidityLocked = contractState.liquidityLocked;
    }
    
    // Mise à jour de l'état
    setContractState(prevState => ({
      ...prevState,
      ...data
    }));
    
    return true;
  }, [contractState, addLog]);
  
  // Récupération des données financières
  const fetchFinancialData = useCallback(async () => {
    if (!contractRef.current) {
      addLog('warn', 'Contrat non initialisé pour fetchFinancialData');
      return false;
    }
    
    addLog('finance', 'Récupération des données financières');
    const contract = contractRef.current;
    const data = {};
    
    // 1. Balance du contrat
    try {
      const balance = await contract.balanceOf(PROXY_ADDRESS);
      data.contractBalance = ethers.utils.formatEther(balance);
      addLog('success', 'Balance du contrat récupérée', { balance: data.contractBalance });
    } catch (balanceErr) {
      addLog('error', 'Échec récupération balance du contrat', { error: balanceErr.message });
      data.contractBalance = contractState.contractBalance; // Garder la valeur actuelle
    }
    
    // 2. Total Burned
    try {
      const burned = await contract.totalBurned();
      data.totalBurned = ethers.utils.formatEther(burned);
      addLog('success', 'Total burned récupéré', { totalBurned: data.totalBurned });
    } catch (burnedErr) {
      addLog('error', 'Échec récupération total burned', { error: burnedErr.message });
      data.totalBurned = contractState.totalBurned; // Garder la valeur actuelle
    }
    
    // 3. Rewards Pool
    try {
      const rewardsPool = await contract.currentRewardsPool();
      data.rewardsPool = ethers.utils.formatEther(rewardsPool);
      addLog('success', 'Rewards pool récupéré', { rewardsPool: data.rewardsPool });
    } catch (rewardsErr) {
      addLog('error', 'Échec récupération rewards pool', { error: rewardsErr.message });
      data.rewardsPool = contractState.rewardsPool; // Garder la valeur actuelle
    }
    
    // 4. Total Staked
    try {
      const totalStaked = await contract.totalStaked();
      data.totalStaked = ethers.utils.formatEther(totalStaked);
      addLog('success', 'Total staked récupéré', { totalStaked: data.totalStaked });
    } catch (stakedErr) {
      addLog('error', 'Échec récupération total staked', { error: stakedErr.message });
      data.totalStaked = contractState.totalStaked; // Garder la valeur actuelle
    }
    
    // 5. Total LP Staked
    try {
      const totalLPStaked = await contract.totalLPStaked();
      data.totalLPStaked = ethers.utils.formatEther(totalLPStaked);
      addLog('success', 'Total LP staked récupéré', { totalLPStaked: data.totalLPStaked });
    } catch (lpStakedErr) {
      addLog('error', 'Échec récupération total LP staked', { error: lpStakedErr.message });
      data.totalLPStaked = contractState.totalLPStaked; // Garder la valeur actuelle
    }
    
    // 6. Max Burn Supply
    try {
      const maxBurnSupply = await contract.MAX_BURN_SUPPLY();
      data.maxBurnSupply = ethers.utils.formatEther(maxBurnSupply);
      
      // Calcul du remaining et du pourcentage
      const totalBurnedValue = data.totalBurned || contractState.totalBurned;
      const totalBurnedBN = ethers.utils.parseEther(totalBurnedValue);
      const maxBurnSupplyBN = maxBurnSupply;
      
      data.burnProgress = parseFloat(totalBurnedValue) / parseFloat(data.maxBurnSupply) * 100;
      data.burnRemaining = ethers.utils.formatEther(maxBurnSupplyBN.sub(totalBurnedBN));
      
      addLog('success', 'Max burn supply récupéré', { 
        maxBurnSupply: data.maxBurnSupply,
        burnProgress: data.burnProgress,
        burnRemaining: data.burnRemaining
      });
    } catch (maxBurnErr) {
      addLog('error', 'Échec récupération max burn supply', { error: maxBurnErr.message });
      // Garder les valeurs actuelles
      data.maxBurnSupply = contractState.maxBurnSupply;
      data.burnProgress = contractState.burnProgress;
      data.burnRemaining = contractState.burnRemaining;
    }
    
    // Mise à jour de l'état
    setContractState(prevState => ({
      ...prevState,
      ...data
    }));
    
    return true;
  }, [contractState, addLog]);
  
  // Récupération des données Uniswap
  const fetchUniswapData = useCallback(async () => {
    if (!uniswapContractRef.current) {
      addLog('warn', 'Contrat Uniswap non initialisé pour fetchUniswapData');
      return false;
    }
    
    addLog('uniswap', 'Récupération des données Uniswap');
    const uniswapContract = uniswapContractRef.current;
    
    let uniswapData = {
      gr33dReserve: contractState.uniswapReserves.gr33dReserve,
      ethReserve: contractState.uniswapReserves.ethReserve,
      totalLiquidity: contractState.uniswapReserves.totalLiquidity,
      ethPrice: contractState.uniswapReserves.ethPrice
    };
    
    try {
      // 1. Récupération du prix ETH
      const ethPrice = await fetchEthPrice();
      uniswapData.ethPrice = ethPrice;
      
      // 2. Récupération des tokens et réserves
      const [token0, reserves] = await Promise.all([
        uniswapContract.token0(),
        uniswapContract.getReserves()
      ]);
      
      const isToken0GR33D = token0.toLowerCase() === PROXY_ADDRESS.toLowerCase();
      
      // Identification des réserves pour chaque token
      if (isToken0GR33D) {
        uniswapData.gr33dReserve = ethers.utils.formatEther(reserves[0]);
        uniswapData.ethReserve = ethers.utils.formatEther(reserves[1]);
      } else {
        uniswapData.gr33dReserve = ethers.utils.formatEther(reserves[1]);
        uniswapData.ethReserve = ethers.utils.formatEther(reserves[0]);
      }
      
      // 3. Calcul de la liquidité totale selon la formule exacte d'Uniswap
      const gr33dReserveNum = parseFloat(uniswapData.gr33dReserve);
      const ethReserveNum = parseFloat(uniswapData.ethReserve);
      
      // Calcul du prix GR33D en USD
      const gr33dPriceUSD = (ethReserveNum * ethPrice) / gr33dReserveNum;
      
      // Calcul de la liquidité selon formule Uniswap V2 précise
      const totalLiquidityUSD = 2 * Math.sqrt(gr33dReserveNum * ethReserveNum * gr33dPriceUSD * ethPrice);
      uniswapData.totalLiquidity = totalLiquidityUSD.toString();
      
      addLog('success', 'Données Uniswap récupérées', uniswapData);
      
      // Mise à jour de l'état
      setContractState(prevState => ({
        ...prevState,
        uniswapReserves: uniswapData
      }));
      
      return true;
    } catch (error) {
      addLog('error', 'Échec récupération données Uniswap', { error: error.message });
      
      // Garder les données précédentes en cas d'échec
      setContractState(prevState => ({
        ...prevState,
        uniswapReserves: {
          ...prevState.uniswapReserves,
          ethPrice: uniswapData.ethPrice || prevState.uniswapReserves.ethPrice
        }
      }));
      
      return false;
    }
  }, [contractState, uniswapContractRef, fetchEthPrice, addLog]);
  
  // Fonction principale de chargement de toutes les données
  const loadAllData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      addLog('load', '⭐ Début du chargement des données du dashboard ⭐');
      
      // Si aucun provider n'est disponible, essayer d'en initialiser un
      if (!provider) {
        addLog('warn', 'Aucun provider disponible, utilisation des données en cache');
        setIsLoading(false);
        return;
      }
      
      // Initialisation des contrats si nécessaire
      if (!contractRef.current) {
        const initResult = await initializeContracts();
        if (!initResult.success) {
          addLog('warn', `Initialisation des contrats échouée: ${initResult.error}`);
          // Continuer quand même avec les valeurs par défaut
        }
      }
      
      // Essayer de charger les données si les contrats sont initialisés
      if (contractRef.current) {
        // Chargement séquentiel pour traçabilité et fiabilité
        await fetchBasicContractInfo();
        await fetchContractStates();
        await fetchFinancialData();
        
        // Chargement des données Uniswap si le contrat est disponible
        if (uniswapContractRef.current) {
          await fetchUniswapData();
        }
      }
      
      // Stocker les données dans le cache local
      try {
        const cacheData = {
          ...contractState,
          cacheTimestamp: Date.now()
        };
        localStorage.setItem('gr33d_dashboard_cache', JSON.stringify(cacheData));
        addLog('cache', 'Données mises en cache avec succès');
      } catch (cacheError) {
        addLog('error', 'Erreur lors de la mise en cache des données', { error: cacheError.message });
      }
      
      setLastUpdateTime(new Date());
      addLog('load', '✅ Chargement des données du dashboard terminé avec succès');
      
    } catch (error) {
      addLog('error', '❌ Erreur lors du chargement des données', { error: error.message });
      setError("Une erreur est survenue lors du chargement des données: " + error.message);
    } finally {
      setIsLoading(false);
    }
  }, [
    provider,
    contractState,
    initializeContracts,
    fetchBasicContractInfo,
    fetchContractStates,
    fetchFinancialData,
    fetchUniswapData,
    addLog
  ]);
  
  // Chargement initial des données
  useEffect(() => {
    if (provider) {
      loadAllData();
      
      // Rechargement périodique
      const interval = setInterval(loadAllData, 3600000); // 5 minutes
      
      return () => clearInterval(interval);
    }
  }, [provider, loadAllData]);
  
  return {
    // États principaux
    isLoading,
    error,
    lastUpdateTime,
    
    // Données
    contractState,
    
    // Actions
    refreshData: loadAllData,
  };
};

export default useDashboard;
